// "Is this for a show?" Same search the receipt review page runs
// (/receipts/event-options): with nothing typed, your own shows nearest the
// date; typing searches every show. Built on the shared combobox so the list
// takes the keyboard.

import React, { useEffect, useMemo, useState } from 'react';
import EntityCombobox, { type ComboboxOption } from '../components/EntityCombobox';
import { buttonClass } from '../components/ui/Button';
import { modalInput } from '../components/review/fieldStyles';
import { formatEventDate } from '../utils/date-utils';
import { useRequisitionsApi, type EventOption } from './requisitionsApi';

interface Props {
  id?: string;
  /** Visible label, and the field's accessible name. */
  label: string;
  labelClassName?: string;
  value: { id: string; name: string } | null;
  /** YYYY-MM-DD the nearest-shows list centers on. */
  date: string;
  onChange: (next: { id: string; name: string } | null) => void;
  disabled?: boolean;
}

const EventField: React.FC<Props> = ({ id, label, labelClassName, value, date, onChange, disabled }) => {
  const reqApi = useRequisitionsApi();
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<EventOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let stale = false;
    setSearching(true);
    const q = query.trim();
    const t = setTimeout(async () => {
      try {
        const list = await reqApi.eventOptions(q ? { q } : { date });
        if (!stale) {
          setRows(list);
          setFailed(false);
        }
      } catch {
        if (!stale) {
          setRows([]);
          setFailed(true);
        }
      } finally {
        if (!stale) setSearching(false);
      }
    }, 250);
    return () => {
      stale = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, date]);

  const options = useMemo<ComboboxOption[]>(() => {
    const list: ComboboxOption[] = [];
    const seen = new Set<string>();
    // The chosen show first and always: the combobox reads its resting label
    // off the options, so a selection missing from them leaves the box blank.
    if (value) {
      list.push({ id: value.id, label: value.name });
      seen.add(value.id);
    }
    rows.forEach((e) => {
      if (seen.has(e.id)) return;
      seen.add(e.id);
      list.push({
        id: e.id,
        label: e.name,
        sublabel: [e.event_date ? formatEventDate(e.event_date) : null, e.location]
          .filter(Boolean)
          .join(', ') || null,
      });
    });
    return list;
  }, [rows, value]);

  const message = failed
    ? 'Could not load shows. Type to try again.'
    : searching
      ? 'Searching…'
      : query.trim()
        ? 'No show matches that.'
        : 'No shows near that date. Type to search all of them.';

  return (
    <div className="flex items-end gap-2">
      <EntityCombobox
        id={id}
        label={label}
        labelClassName={labelClassName}
        value={value ? value.id : ''}
        options={options}
        onSelect={(o) => onChange(o ? { id: String(o.id), name: o.label } : null)}
        onQueryChange={(q) => {
          setQuery(q);
          // Typing over a chosen show un-chooses it, so the form never holds
          // one show while the box reads another.
          if (value && q && q !== value.name) onChange(null);
        }}
        filterOptions={false}
        placeholder="Search shows, or pick one of yours"
        disabled={disabled}
        className="min-w-0 flex-1"
        inputClassName={modalInput}
        emptyMessage={message}
        noOptionsMessage={message}
      />
      {value && !disabled && (
        <button
          type="button"
          onClick={() => onChange(null)}
          aria-label={`Clear show ${value.name}`}
          className={`${buttonClass('quiet')} shrink-0`}
        >
          Clear
        </button>
      )}
    </div>
  );
};

export default EventField;
